import React, { Component, lazy, Suspense } from 'react';
import postal from 'postal';
import { string, object, bool } from 'prop-types';
import { Menu } from '@material-ui/icons';
import styles from './styles/styles.module.scss';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { wrapEventHandler } from '../../utils/handlers';
import { generateNavigationProps } from '../../utils/HeaderHelper';

const SideDrawer = lazy(() => import('../SideDrawer'));

function updateLinkRef(linkPath) {
  return linkPath.replace('?ref=header', '?ref=mobile-header');
}

function updateDropdownLinkRef(link) {
  if (!link.dropdown) {
    return undefined;
  }

  return link.dropdown.map(dropdownLink => {
    return { ...dropdownLink, linkPath: updateLinkRef(dropdownLink.linkPath) };
  });
}

function parseLinkRef(links) {
  if (!links) {
    return undefined;
  }

  return links.map(link => {
    return { ...link, dropdown: updateDropdownLinkRef(link), linkPath: updateLinkRef(link.linkPath) };
  });
}

class NavigationDrawer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      slimMode: false,
      ...generateNavigationProps(props), // props passed in for state for logging in and out without a page refresh
      isMounted: false
    };
  }

  componentDidMount() {
    if (this.props.messagingChannel) {
      this.props.messagingChannel.subscribe('navigation.slim', this.toggleSlimNavigation);
      this.props.messagingChannel.subscribe('navigation.loginUser', this.updateForLoggedInUser);
    }
    this.setState({ isMounted: true });
  }

  componentWillUnmount() {
    // This check for "unsubscribe" prevents weird HMR issues
    if (this.props.messagingChannel && typeof this.props.messagingChannel.unsubscribe === 'function') {
      this.props.messagingChannel.unsubscribe('navigation.slim');
      this.props.messagingChannel.unsubscribe('navigation.loginUser');
    }
  }

  updateForLoggedInUser = data => {
    const loggedInState = generateNavigationProps(data);
    this.setState({ ...loggedInState });
  };

  toggleSlimNavigation = ({ slimMode }) => {
    this.setState({ slimMode });
  };

  toggleIsOpen = () => {
    const { isOpen } = this.state;
    this.setState({ isOpen: !isOpen }, this.toggleWindowLockAndTrack);
  };

  toggleWindowLockAndTrack() {
    _gaq.push(['_trackEvent', 'Mobile Header', 'Toggle', window.location.pathname]);
    this.toggleWindowLock();
  }

  toggleWindowLock() {
    if (this.state.isOpen) {
      document.querySelector('html').classList.add('mobile-screen-lock');
    } else {
      document.querySelector('html').classList.remove('mobile-screen-lock');
    }
  }

  parseStateForLinks() {
    const {
      links: { explore, plan, history, help, downloadApp, community, saved },
      user
    } = this.state;
    const links = [help, downloadApp];
    const dropdownLinks = this.props.featureFlags.communityFeatureFlag ? [explore, community, saved] : [explore, plan, history];

    return { dropdownLinks: parseLinkRef(dropdownLinks), links: parseLinkRef(links), user };
  }

  getMobileLinks() {
    // here we need to parse the links to have ?ref=mobile-header for tracking
    // parsing this on the client so the server doesn't need to generate a new routes
    const { links, dropdownLinks, user } = this.parseStateForLinks();

    if (user.dropdown.length > 0) {
      const myAccount = { dropdown: user.dropdown, label: StringConstantsMixin.strings.MY_ACCOUNT, linkPath: '#' };
      dropdownLinks.push(myAccount);
    }

    return { dropdownLinks, links };
  }

  sideDrawerProps() {
    const { user } = this.state;
    const sideDrawerProps = this.getMobileLinks();
    return {
      ...sideDrawerProps,
      adminUser: this.props.adminUser,
      isOpen: this.state.isOpen,
      toggleIsOpen: wrapEventHandler(this.toggleIsOpen),
      user
    };
  }

  renderSideDrawer() {
    if (!this.state.isMounted) {
      return null;
    }

    return (
      <Suspense fallback={null}>
        <SideDrawer {...this.sideDrawerProps()} proButton={this.state.proButton} />
      </Suspense>
    );
  }

  render() {
    if (this.state.slimMode) {
      return <div className={styles.mobileLeftSpacer} />;
    }

    return (
      <>
        <a href="#" onClick={wrapEventHandler(this.toggleIsOpen)} className={styles.navigationMenuLink} data-test-id="navigationDrawer-menuButton">
          <Menu className={styles.navigationMenu} />
        </a>
        {this.renderSideDrawer()}
      </>
    );
  }
}

NavigationDrawer.propTypes = {
  adminUser: bool,
  currentUser: object,
  displayMetric: bool.isRequired,
  forceProLogo: bool,
  languageRegionCode: string,
  messagingChannel: object,
  mobileBrowser: bool.isRequired,
  proUser: bool,
  tabletBrowser: bool.isRequired
};

NavigationDrawer.defaultProps = {
  adminUser: null,
  currentUser: null,
  forceProLogo: false,
  languageRegionCode: 'en-US',
  messagingChannel: postal.channel('navigation_messagingChannel'),
  proUser: null
};

export default NavigationDrawer;
